// ========================================================
// FAST HIT Website — Tournament Spotlight Component
// Real-time Firestore tournaments — no dummy data
// Premium glassmorphism cards with animated gradients
// ========================================================

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Trophy, Calendar, MapPin, Users, ArrowRight, ShieldCheck, Plus, Sparkles } from 'lucide-react';
import { getSpotlightTournaments } from '../../services/tournamentsService';
import './TournamentSpotlight.css';

export function TournamentSpotlight() {
  const { data: tournaments = [], isLoading } = useQuery({
    queryKey: ['spotlightTournaments'],
    queryFn: () => getSpotlightTournaments(3),
    staleTime: 1000 * 60 * 5,
  });

  const hasTournaments = tournaments.length > 0;

  return (
    <section className="tourney-spotlight" aria-labelledby="tourney-title">
      <div className="tourney-spotlight__container">
        {/* Header */}
        <div className="tourney-spotlight__header">
          <div>
            <div className="section-eyebrow">
              <Trophy size={14} className="text-cyan" />
              <span>Tournament Arena</span>
            </div>
            <h2 id="tourney-title" className="section-title">
              Tournaments & Championship Fixtures
            </h2>
            <p className="section-subtitle">
              Compete in official tournaments, track live standings, and monitor team qualification.
            </p>
          </div>

          <Link to="/tournaments" className="btn btn--outline btn--sm">
            <span>Explore Tournaments</span>
            <ArrowRight size={14} />
          </Link>
        </div>

        {/* Loading Skeletons */}
        {isLoading && (
          <div className="tourney-spotlight__grid">
            {[1, 2].map((i) => (
              <div key={i} className="tourney-card--skeleton card">
                <div className="skeleton skeleton--title" />
                <div className="skeleton skeleton--text" />
                <div className="skeleton skeleton--text" style={{ width: '50%' }} />
              </div>
            ))}
          </div>
        )}

        {/* Tournaments Grid */}
        {!isLoading && hasTournaments && (
          <div className="tourney-spotlight__grid">
            {tournaments.map((tourney) => {
              const isRegOpen = tourney.status === 'registration';
              const isLive = tourney.status === 'live';

              return (
                <div key={tourney.id} className="tourney-card card">
                  <div className="tourney-card__top">
                    <span
                      className={`tourney-card__status ${
                        isLive
                          ? 'tourney-card__status--live'
                          : isRegOpen
                          ? 'tourney-card__status--reg'
                          : ''
                      }`}
                    >
                      {isLive ? '🔴 LIVE EVENT' : isRegOpen ? 'REGISTRATIONS OPEN' : tourney.status.toUpperCase()}
                    </span>
                    <span className="tourney-card__format font-mono">{tourney.format}</span>
                  </div>

                  <h3 className="tourney-card__name">{tourney.name}</h3>
                  <p className="tourney-card__season text-muted text-xs">{tourney.season}</p>

                  {tourney.prizePool && (
                    <div className="tourney-card__prize">
                      <Trophy size={14} className="text-cyan" />
                      <span>{tourney.prizePool}</span>
                    </div>
                  )}

                  <div className="tourney-card__meta">
                    <div className="tourney-card__meta-item">
                      <MapPin size={13} className="text-muted" />
                      <span>{tourney.location}</span>
                    </div>
                    <div className="tourney-card__meta-item">
                      <Calendar size={13} className="text-muted" />
                      <span>{new Date(tourney.startDate).toLocaleDateString()}</span>
                    </div>
                    <div className="tourney-card__meta-item">
                      <Users size={13} className="text-muted" />
                      <span>{tourney.teamsCount} Teams Participating</span>
                    </div>
                  </div>

                  <div className="tourney-card__actions">
                    <Link
                      to={`/tournaments/${tourney.id}`}
                      className="btn btn--outline btn--sm w-full"
                    >
                      <span>View Standings & Fixtures</span>
                      <ArrowRight size={14} />
                    </Link>
                    {isRegOpen && (
                      <Link
                        to={`/tournaments/${tourney.id}`}
                        className="btn btn--primary btn--sm w-full"
                      >
                        <ShieldCheck size={14} />
                        <span>Register Team</span>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !hasTournaments && (
          <div className="tourney-spotlight__empty">
            <div className="tourney-spotlight__empty-icon">
              <Sparkles size={40} />
            </div>
            <h3 className="tourney-spotlight__empty-title">No Active Tournaments</h3>
            <p className="tourney-spotlight__empty-desc">
              Cricket tournaments will appear here when organizers create them on FAST HIT.
              Want to host your own league or knockout cup?
            </p>
            <Link to="/app/tournaments" className="btn btn--primary btn--sm btn--pill">
              <Plus size={14} />
              <span>Create Tournament</span>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
